import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Video, CalendarClock, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConsultModal({ isOpen, onClose, provider }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState("instant");
  const [scheduledAt, setScheduledAt] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!user) {
      toast.info("Please sign in to start a virtual consult");
      onClose();
      navigate("/auth");
      return;
    }
    if (mode === "scheduled" && !scheduledAt) {
      toast.error("Please pick a date and time");
      return;
    }
    setIsCreating(true);
    try {
      const { data } = await axios.post(`${API}/consults`, {
        provider_id: provider.id,
        mode,
        scheduled_at: mode === "scheduled" ? new Date(scheduledAt).toISOString() : null,
        duration_minutes: 45,
      });
      if (mode === "instant") {
        toast.success("Your video room is ready!");
        onClose();
        navigate(`/consult/${data.id}`);
      } else {
        toast.success("Consult scheduled! Find it in your portal with the provider's join link.", { duration: 7000 });
        onClose();
        navigate("/portal");
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-primary">
            <Video className="w-5 h-5" /> Virtual Consult
          </DialogTitle>
          <DialogDescription className="font-body">
            Meet face-to-face with {provider?.name} by secure video — right here in the app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="consult-mode-instant"
              onClick={() => setMode("instant")}
              className={`p-4 rounded-xl border-2 text-left transition-colors ${
                mode === "instant" ? "border-primary bg-primary/5" : "border-[#E2E8F0] hover:border-primary/40"
              }`}
            >
              <Zap className={`w-5 h-5 mb-2 ${mode === "instant" ? "text-primary" : "text-text-secondary"}`} />
              <p className="font-body font-bold text-sm text-text-primary">Start Now</p>
              <p className="font-body text-xs text-text-secondary mt-1">Open a video room immediately</p>
            </button>
            <button
              data-testid="consult-mode-scheduled"
              onClick={() => setMode("scheduled")}
              className={`p-4 rounded-xl border-2 text-left transition-colors ${
                mode === "scheduled" ? "border-primary bg-primary/5" : "border-[#E2E8F0] hover:border-primary/40"
              }`}
            >
              <CalendarClock className={`w-5 h-5 mb-2 ${mode === "scheduled" ? "text-primary" : "text-text-secondary"}`} />
              <p className="font-body font-bold text-sm text-text-primary">Schedule</p>
              <p className="font-body text-xs text-text-secondary mt-1">Pick a date & time</p>
            </button>
          </div>

          {mode === "scheduled" && (
            <div>
              <Label className="font-body font-semibold">Date & Time</Label>
              <Input
                data-testid="consult-datetime-input"
                type="datetime-local"
                value={scheduledAt}
                min={new Date(Date.now() + 10 * 60000).toISOString().slice(0, 16)}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="mt-1 h-12 font-body"
              />
              <p className="text-xs text-text-secondary font-body mt-1">
                Consults last up to 45 minutes. The room opens 10 minutes early.
              </p>
            </div>
          )}

          <p className="text-xs text-text-secondary font-body bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
            {mode === "instant"
              ? "You'll get a secure join link to share with the provider so they can hop on."
              : "After scheduling, you'll get a secure join link for the provider — share it by phone, text, or email."}
          </p>

          <Button
            data-testid="consult-create-btn"
            onClick={handleCreate}
            disabled={isCreating}
            className="w-full min-h-[48px] bg-primary text-white hover:bg-[#238C82] rounded-full font-bold font-body"
          >
            {isCreating ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "instant" ? <><Video className="w-4 h-4 mr-2" /> Start Video Consult</> : <><CalendarClock className="w-4 h-4 mr-2" /> Schedule Consult</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
